import React from 'react';
import styled from 'styled-components';

import ArrowsButtonRightMin from '@/components/atom/icon/Arrow/ArrowsButtonRightMin';
import ClickLogger from '@/components/common/ClickLogger';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import EmptyView from '@/components/molecules/detail/EmptyView/EmptyView';
import CategoryHeader from '@/components/templates/doctor/detail/components/CategoryHeader/CategoryHeader';
import DoctorReviewSkeleton from '@/components/templates/doctor/detail/components/DoctorReview/DoctorReviewSkeleton';
import DoctorReviewSummary from '@/components/templates/doctor/detail/components/DoctorReview/DoctorReviewSummary';
import ReviewSortKeySelect from '@/components/templates/doctor/detail/components/DoctorReview/ReviewSortKeySelect';
import Section from '@/components/templates/doctor/detail/components/Section/Section';
import { DoctorDetailTabType } from '@/components/templates/doctor/detail/useDoctorDetailViewModel';
import { DoctorReviewsViewModel } from '@/components/templates/doctor/detail/useDoctorReviewsViewModel';
import ReviewItem from '@/components/templates/review/ReviewItem';
import { DoctorDetailLog } from '@/constants/logs/doctor';
import useReviewReportModal from '@/hooks/review/useReviewReportModal';
import { EReviewType, ReviewTargetType } from '@/models/review/review';
import theme from '@/styles/theme';
import { formatPriceComma } from '@/utils/format';

import BestReviewItem from './BestReviewItem';

interface Props {
  isMobile: boolean;
  doctorReviewsViewModel: DoctorReviewsViewModel;
}

const DOCTOR_REVIEW_SHOW_LENGTH = 3;
const DoctorReview = ({ isMobile, doctorReviewsViewModel }: Props) => {
  const { showReportModal, openReportModal, onCloseReportModal, reportItems } = useReviewReportModal();

  return (
    <>
      <AnchorArea id={DoctorDetailTabType.Review} />
      <div>
        {!doctorReviewsViewModel.reviews?.length ? (
          <Section>
            <Section.Title title={'진료 후기'} />
            <EmptyView>
              <EmptyText>아직 남겨진 후기가 없어요.</EmptyText>
            </EmptyView>
          </Section>
        ) : (
          <Wrapper>
            <Section.Title title={'진료 후기'}>
              <TotalReviewCount>{formatPriceComma(doctorReviewsViewModel.totalCount)}</TotalReviewCount>
            </Section.Title>
            {doctorReviewsViewModel.bestDoctorReview && (
              <>
                <BestReviewItem
                  onClickMenu={openReportModal}
                  clickLikeReview={doctorReviewsViewModel.clickLikeReview}
                  review={doctorReviewsViewModel.bestDoctorReview}
                  isOtherUsersPrivateReview={doctorReviewsViewModel.isOtherUsersPrivateReview}
                />
                <Gap />
              </>
            )}
            <ReviewSummaryWrapper alignCenter={isMobile}>
              <DoctorReviewSummary
                averageRating={`${doctorReviewsViewModel.doctorDetail?.doctor.averageRating.toFixed(1)}`}
                reviewTagGroups={doctorReviewsViewModel.getReviewStatisticGroupsForSummary()}
                hideReviewSummary={doctorReviewsViewModel.isReviewNotEnough()}
              />
            </ReviewSummaryWrapper>
            <Gap />
            <CategoryAndSortWrapper>
              <CategoryHeader>
                {doctorReviewsViewModel.getReviewCategories().map((category) => (
                  <CategoryHeader.Item
                    key={`review-category-${category.id}-${category.diagnosisType}`}
                    onClick={() => doctorReviewsViewModel.selectReviewCategory(category)}
                    isSelected={doctorReviewsViewModel.isReviewCategorySelected(category)}
                  >
                    <p>{category.name}</p>
                    {doctorReviewsViewModel.getReviewCountByCategory(category)}
                  </CategoryHeader.Item>
                ))}
              </CategoryHeader>
              <SortHeader>
                <ReviewSortKeySelect doctorReviewsViewModel={doctorReviewsViewModel} />
              </SortHeader>
            </CategoryAndSortWrapper>
            <ReviewList>
              {!doctorReviewsViewModel.reviews.length && doctorReviewsViewModel.isFetching ? (
                <DoctorReviewSkeleton />
              ) : (
                doctorReviewsViewModel.reviews
                  .slice(0, DOCTOR_REVIEW_SHOW_LENGTH)
                  .map((review) => (
                    <ReviewItem
                      key={`review-${review.id}`}
                      review={review}
                      onClickMenu={openReportModal}
                      isHideContent={doctorReviewsViewModel.isOtherUsersPrivateReview(review)}
                      clickLikeReview={() =>
                        doctorReviewsViewModel.clickLikeReview(Number(review.id), review.isLiked, EReviewType.NORMAL)
                      }
                      reviewTargetType={ReviewTargetType.DOCTOR}
                    />
                  ))
              )}
            </ReviewList>
            {doctorReviewsViewModel.totalCount > DOCTOR_REVIEW_SHOW_LENGTH && (
              <ClickLogger
                params={{
                  eventName: `${
                    DoctorDetailLog.click_review_all
                  }_${doctorReviewsViewModel.selectedReviewCategory.diagnosisType?.toLowerCase()}_${
                    doctorReviewsViewModel.selectedReviewCategory.id
                  }`,
                }}
              >
                <ViewAllButton onClick={doctorReviewsViewModel.onClickViewAll}>
                  전체보기
                  <ArrowsButtonRightMin color={theme.color.gray600} />
                </ViewAllButton>
              </ClickLogger>
            )}
            {reportItems && (
              <ReportSelectModal isVisible={showReportModal} selectList={reportItems} onClose={onCloseReportModal} />
            )}
          </Wrapper>
        )}
        <Section.Divider />
      </div>
    </>
  );
};

export default DoctorReview;

const Wrapper = styled.div`
  padding: 28px 0;
`;

const TotalReviewCount = styled.div`
  margin-left: 8px;
  ${({ theme }) => `
    ${theme.typography.UI19SB};
    color: ${theme.color.primary700};
  `}
`;

const ReviewSummaryWrapper = styled.div<{ alignCenter: boolean }>`
  width: 100%;
  padding: 20px 0;

  ${({ alignCenter }) => `
    justify-content ${alignCenter ? 'center' : 'flex-start'}
  `}
`;

const ReviewList = styled.div`
  width: 100%;
`;

const CategoryAndSortWrapper = styled.div`
  width: 100%;
  padding-top: 20px;
`;

const SortHeader = styled.div``;

const EmptyText = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI18M};
    color:${theme.color.gray700};
  `}
`;

const AnchorArea = styled.div`
  height: 108px;
  margin-top: -108px;
  display: block;
  visibility: hidden;
`;

const ViewAllButton = styled.button`
  width: 100%;
  margin: 16px 0;

  svg {
    margin-left: 8px;
  }

  ${({ theme }) => `
    ${theme.typography.UI16M};
    color:${theme.color.gray600};
  `}
`;

const Gap = styled.div`
  height: 8px;
  background-color: ${theme.color.gray30};
`;
